import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { useHydrated } from "@/hooks/useHydrated";

import { LandingRoom } from "./LandingRoom";

/**
 * The demo, run where the visitor is standing (#615).
 *
 * The first screen holds a drawing of a room; *try the demo* replaces that
 * drawing, in the same place, with the real thing: the demo the product loads
 * at `/demo`, not a second one written for the page — a demonstration that is
 * not the product demonstrates nothing (#942).
 *
 * **No navigation, and nothing fetched until the press.** The demo is behind a
 * `lazy`, so a page nobody has pressed downloads neither the engine nor the
 * plugin, and the drawing is the fallback, so the space never collapses while
 * the demo arrives.
 */
const DemoRoom = lazy(() => import("@/plugins/demo/DemoRoom"));

export function LandingStage({ started }: { started: boolean }) {
  if (!started) return <LandingRoom />;
  return (
    <div data-testid="landing-demo-stage">
      <Suspense fallback={<LandingRoom />}>
        <DemoRoom />
      </Suspense>
    </div>
  );
}

export function LandingDemoButton({
  onStart,
  label,
  testId = "landing-demo",
}: {
  onStart: () => void;
  label?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  // The page is drawn by the server, so the button is on screen before its
  // press can do anything. Held until then, a press is never swallowed.
  const live = useHydrated();
  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      data-testid={testId}
      disabled={!live}
      onClick={onStart}
    >
      {label ?? t("landing.hero.demo")}
    </Button>
  );
}
